import { cn } from '@/lib/utils';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { WithdrawalSettlementDto } from '@/features/dashboard/bookingChanges/types/dto/WithdrawalDto.ts';
import { formatMoneyExact } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { isZeroMoney } from '@/features/dashboard/bookingChanges/utils/money.ts';
import {
  SETTLEMENT_LABELS,
  settlementScopeSentence,
} from './withdrawalCopy.ts';

const Row = ({
  label,
  value,
  strong = false,
  tone,
}: {
  label: string;
  value: MoneyValueDto;
  strong?: boolean;
  tone?: 'refund' | 'debt';
}) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-sm text-neutral-600">{label}</span>
    <span
      className={cn(
        'tabular-nums',
        strong ? 'text-[15px] font-bold' : 'text-sm font-semibold',
        tone === 'refund' && 'text-success-300',
        tone === 'debt' && 'text-error-300',
        !tone && 'text-ink',
      )}
    >
      {formatMoneyExact(value)}
    </span>
  </div>
);

interface WithdrawalSettlementPanelProps
  extends React.HTMLAttributes<HTMLDivElement> {
  settlement: WithdrawalSettlementDto;
}

/**
 * Rozliczenie rezygnacji, po jednym bloku na walutę — kwoty w różnych walutach
 * nie sumują się, więc nie ma tu jednej liczby na dole.
 *
 * Zwrot i dług pokazujemy tylko wtedy, gdy są: obie kwoty są pochodne i jedna
 * z nich jest z definicji zerem, a zero opisane etykietą czyta się jak zdarzenie.
 */
const WithdrawalSettlementPanel = ({
  settlement,
  className,
  ...props
}: WithdrawalSettlementPanelProps) => (
  <div className={cn('flex flex-col gap-3', className)} {...props}>
    <p className="text-pretty text-sm text-neutral-600">
      {settlementScopeSentence(settlement)}
    </p>

    {settlement.lines.map((line) => (
      <div
        key={line.share.currency}
        className="rounded-[11px] border border-neutral-200 px-[18px] py-3"
      >
        <div className={columnLabelClass}>{line.share.currency}</div>
        <div className="mt-1.5">
          <Row label={SETTLEMENT_LABELS.share} value={line.share} />
          {!isZeroMoney(line.fee) && (
            <Row label={SETTLEMENT_LABELS.fee} value={line.fee} />
          )}
          <Row label={SETTLEMENT_LABELS.paid} value={line.paid} />
          <Row
            label={SETTLEMENT_LABELS.liabilityAfter}
            value={line.liabilityAfter}
          />
          {!isZeroMoney(line.refund) && (
            <Row
              label={SETTLEMENT_LABELS.refund}
              value={line.refund}
              strong
              tone="refund"
            />
          )}
          {!isZeroMoney(line.debt) && (
            <Row
              label={SETTLEMENT_LABELS.debt}
              value={line.debt}
              strong
              tone="debt"
            />
          )}
        </div>
      </div>
    ))}
  </div>
);

export default WithdrawalSettlementPanel;
